if (!customElements.get("collection-load-more")) {
  const restore = window.BsrCollectionLoadMoreRestore = {
    pending: false,
    promise: Promise.resolve({ status: "idle" }),
    status: "idle",
    scrollY: null,
  };
  class CollectionLoadMore extends HTMLElement {
    constructor() {
      super();

      this.initialPage = this.pageNumber(window.location.href);
      this.pagesLoaded = 1;
      this.restoreGeneration = 0;
      this.onProductClick = this.onProductLinkClick.bind(this);
      this.onShow = this.onPageShow.bind(this);
      this.onHide = () => {
        this.pageHidden = true;
        this.restoreGeneration++;
        this.controller?.abort();
        if (this.resizeTimer) clearTimeout(this.resizeTimer);
      };
      this.onResize = () => {
        if (!this.cachedRestore) return;
        this.restoreGeneration++;
        this.controller?.abort();
        if (this.resizeTimer) clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.resumeCachedRefresh(), 150);
      };
      this.onReady = () => this.onPageShow({ persisted: false });
      this.onSectionLoad = () => this.setProperties();

      this.addEventListener("click", (e) => {
        const button = e.target.closest(".js-btn-load-more");
        if (!button || button !== this.btnLoadMore) return;

        e.preventDefault();
        if (!this.loading) this.loadMoreProducts().catch((error) => {
          if (error.name !== "AbortError") {
            console.error(error);
            alert(error.message);
          }
        });
      });
    }

    connectedCallback() {
      this.setProperties();
      document.addEventListener("click", this.onProductClick, true);
      window.addEventListener("pageshow", this.onShow);
      window.addEventListener("pagehide", this.onHide);
      window.addEventListener("resize", this.onResize);
      document.addEventListener("DOMContentLoaded", this.onReady, { once: true });
      if (document.readyState !== "loading") queueMicrotask(this.onReady);
      window.addEventListener("shopify:section:load", this.onSectionLoad);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.cachedRestore = null;
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      document.removeEventListener("click", this.onProductClick, true);
      window.removeEventListener("pageshow", this.onShow);
      window.removeEventListener("pagehide", this.onHide);
      window.removeEventListener("resize", this.onResize);
      document.removeEventListener("DOMContentLoaded", this.onReady);
      window.removeEventListener("shopify:section:load", this.onSectionLoad);
    }

    pageNumber(value) {
      const number = Number(new URL(value, window.location.href).searchParams.get("page"));
      return Number.isInteger(number) && number > 0 ? number : 1;
    }

    setProperties() {
      const grid = document.querySelector("#product-grid");
      if (this.productGrid && this.productGrid !== grid) {
        this.controller?.abort();
        this.cachedRestore = null;
        this.initialPage = this.pageNumber(window.location.href);
        this.pagesLoaded = 1;
        this.initialCardCount = null;
      }
      this.productGrid = grid;
      if (!this.initialCardCount && grid) this.initialCardCount = grid.children.length;
      this.btnLoadMore = this.querySelector(".js-btn-load-more");
      this.isLastPage = this.classList.contains("hidden") || !this.btnLoadMore?.dataset.href;
    }

    onProductLinkClick(e) {
      const link = e.target.closest("#product-grid a[href*='/products/']");
      if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey || link.target === "_blank") return;

      try {
        sessionStorage.setItem(
          "clm_scroll_state",
          JSON.stringify({
            collectionUrl: window.location.pathname + window.location.search,
            initialPage: this.initialPage,
            scrollY: window.scrollY,
            pagesLoaded: this.pagesLoaded,
          })
        );
      } catch (_) {}
      const cache = window.BsrCollectionReturnCache;
      if (cache && this.productGrid && !this.loading && !this.productGrid.querySelector('[data-bsr-cache-placeholder], [data-bsr-cached-card]')) {
        cache.save('grid', this.productGrid.dataset.id || 'product-grid', {
          rows: cache.serializeCards(this.productGrid), pagesLoaded: this.pagesLoaded,
          initialPage: this.initialPage, pageSize: this.initialCardCount, next: this.btnLoadMore?.dataset.href || '', last: this.isLastPage,
          targetHandle: new URL(link.href).pathname.split('/products/')[1]?.split('/')[0] || ''
        });
      }
    }

    onPageShow(event) {
      if (event.persisted) {
        this.pageHidden = false;
        this.restoreGeneration++;
        this.controller?.abort();
        try { sessionStorage.removeItem("clm_scroll_state"); } catch (_) {}
        restore.pending = false;
        restore.status = "bfcache";
        restore.promise = Promise.resolve({ status: "bfcache" });
        this.resumeCachedRefresh();
        return;
      }

      const navEntry = performance.getEntriesByType("navigation")[0];
      if (navEntry?.type !== "back_forward" || this.loading || this.returnHandled) return;
      this.returnHandled = true;

      const cache = window.BsrCollectionReturnCache;
      const snapshot = cache?.read('grid', this.productGrid?.dataset.id || 'product-grid');
      if (snapshot && snapshot.initialPage === this.initialPage && Number.isInteger(snapshot.pagesLoaded) && snapshot.pagesLoaded > 0 && snapshot.pagesLoaded <= 100 && Array.isArray(snapshot.rows) && snapshot.rows.length) {
        try {
          const result = cache.restoreCards(this.productGrid, snapshot.rows, snapshot.targetHandle);
          this.pagesLoaded = snapshot.pagesLoaded;
          this.applyPageState(snapshot);
          restore.pending = true;
          restore.status = 'pending';
          restore.promise = result.targetReady.then(() => {
            restore.pending = false;
            restore.status = 'ready';
            window.dispatchEvent(new CustomEvent('bsr:collection-load-more-ready', { detail: { status: 'ready' } }));
            return { status: 'ready' };
          });
          this.cachedRestore = { snapshot, complete: result.complete, grid: this.productGrid, url: location.pathname + location.search };
          this.resumeCachedRefresh();
          return;
        } catch (error) { console.warn('Collection cache restore skipped.', error); }
      }

      try {
        const raw = sessionStorage.getItem("clm_scroll_state");
        if (!raw) return;

        const state = JSON.parse(raw);
        const currentUrl = window.location.pathname + window.location.search;

        if (state.collectionUrl !== currentUrl) return;
        if (!Number.isInteger(state.pagesLoaded) || state.pagesLoaded < 1) return;
        if ((state.initialPage || this.pageNumber(state.collectionUrl)) !== this.initialPage) return;

        // Publish synchronously; the shared scroll restorer owns the final scroll.
        restore.pending = true;
        restore.status = "pending";
        restore.scrollY = state.scrollY;
        const generation = ++this.restoreGeneration;
        restore.promise = this.restoreScrollState(state.pagesLoaded).then(() => {
          try { sessionStorage.removeItem("clm_scroll_state"); } catch (_) {}
          return { status: "ready", scrollY: state.scrollY };
        }, (error) => {
          if (error.name !== "AbortError") console.error("Collection restoration failed.", error);
          return { status: error.name === "AbortError" ? "cancelled" : "error", scrollY: state.scrollY, error };
        }).then((result) => {
          if (generation !== this.restoreGeneration) return result;
          restore.pending = false;
          restore.status = result.status;
          window.dispatchEvent(new CustomEvent("bsr:collection-load-more-ready", { detail: result }));
          return result;
        });
      } catch (error) { console.warn("Collection restore state could not be read.", error); }
    }

    pageUrl(value) {
      const url = new URL(value, window.location.href);
      const current = new URL(window.location.href);
      const existingKeys = new Set(url.searchParams.keys());
      for (const [key, value] of current.searchParams) {
        if (key !== "page" && !existingKeys.has(key)) url.searchParams.append(key, value);
      }
      url.hash = "";
      return url;
    }

    async fetchPage(value, signal) {
      const fullUrl = this.pageUrl(value);
      const sectionId = this.productGrid?.dataset.id;
      const requestUrl = new URL(fullUrl);
      if (sectionId) requestUrl.searchParams.set("section_id", sectionId);
      const read = async (url) => {
        const nextPageResponse = await fetch(url.toString(), { signal });
        if (!nextPageResponse.ok) {
          throw new Error(
            `Failed to load more products (${nextPageResponse.status}).`
          );
        }

        const html = new DOMParser().parseFromString(await nextPageResponse.text(), "text/html");
        const grid = html.querySelector("#product-grid");
        const container = html.querySelector("collection-load-more");
        const button = container?.querySelector(".js-btn-load-more");
        return { grid, next: button?.dataset.href, last: !container || container.classList.contains("hidden") || !button?.dataset.href };
      };
      let page = await read(requestUrl);
      // Older templates can omit the requested section; retry only on missing grid.
      if (!page.grid && sectionId) page = await read(fullUrl);
      if (!page.grid) throw new Error("Could not find the next product grid.");
      if (signal.aborted) throw new DOMException("Collection request cancelled.", "AbortError");
      return page;
    }

    applyPageState(page) {
      this.isLastPage = page.last;
      this.classList.toggle("hidden", page.last);
      if (this.btnLoadMore) {
        if (page.next) this.btnLoadMore.dataset.href = page.next;
        else delete this.btnLoadMore.dataset.href;
      }
    }

    resumeCachedRefresh() {
      const saved = this.cachedRestore;
      if (!saved || this.pageHidden || !this.isConnected || saved.grid !== document.querySelector('#product-grid') || saved.url !== location.pathname + location.search) return;
      if (!saved.grid.querySelector('[data-bsr-cached-card], [data-bsr-cache-placeholder]')) { this.cachedRestore = null; return; }
      if (this.cacheRefreshTask) { this.cacheRefreshQueued = true; return; }
      this.cacheRefreshQueued = false;
      const task = this.refreshCachedPages(saved.snapshot, saved.complete).catch((error) => {
        if (error.name !== 'AbortError') console.warn('Collection cached display remains read-only.', error);
      }).finally(() => {
        if (this.cacheRefreshTask !== task) return;
        this.cacheRefreshTask = null;
        if (this.cacheRefreshQueued) { this.cacheRefreshQueued = false; this.resumeCachedRefresh(); }
      });
      this.cacheRefreshTask = task;
    }

    async refreshCachedPages(snapshot, complete) {
      const grid = this.productGrid;
      const generation = ++this.restoreGeneration;
      this.loading = true;
      this.btnLoadMore?.setAttribute('disabled', 'true');
      const controller = this.controller = new AbortController();
      const { signal } = controller;
      const contextIdentity = window.BsrCollectionReturnCache.identity();
      const current = () => !signal.aborted && generation === this.restoreGeneration && grid === document.querySelector('#product-grid') && contextIdentity === window.BsrCollectionReturnCache.identity();
      const observer = new MutationObserver(() => { if (grid !== document.querySelector('#product-grid')) controller.abort(); });
      observer.observe(grid.parentElement, { childList: true });
      const results = new Array(snapshot.pagesLoaded);
      const targetIndex = snapshot.rows.findIndex((row) => row.url?.split('?')[0].split('/products/')[1] === snapshot.targetHandle);
      const pageSize = snapshot.pageSize > 0 ? snapshot.pageSize : Math.max(1, Math.ceil(snapshot.rows.length / snapshot.pagesLoaded));
      const targetPage = Math.min(snapshot.pagesLoaded - 1, Math.floor(Math.max(0, targetIndex) / pageSize));
      const queue = [targetPage, ...results.keys()].filter((value, index, values) => values.indexOf(value) === index);
      const worker = async () => {
        while (queue.length && current()) {
          const index = queue.shift();
          const url = new URL(location.href);
          url.searchParams.set('page', String(this.initialPage + index));
          results[index] = await this.fetchPage(url, signal);
        }
      };
      try {
        await Promise.all([worker(), worker()]);
        await complete;
        if (!current()) throw new DOMException('Collection cache refresh cancelled.', 'AbortError');
        const end = results.findIndex((page) => page.last);
        const pages = end < 0 ? results : results.slice(0, end + 1);
        for (let index = 0; index < pages.length - 1; index++) {
          if (!pages[index].next || this.pageNumber(pages[index].next) !== this.initialPage + index + 1) throw new Error('Collection page sequence changed.');
        }
        const orderedUrls = pages.flatMap((page) => Array.from(page.grid.querySelectorAll('card-product[data-product-url], .card-product[data-product-url]'), (card) => card.dataset.productUrl));
        if (new Set(orderedUrls).size !== orderedUrls.length) throw new Error('Collection order changed during refresh.');
        const cards = Array.from(grid.querySelectorAll('card-product, .card-product')).filter((card) => !card.dataset.bsrCachePlaceholder);
        const anchor = cards.find((card) => card.getBoundingClientRect().bottom > 0 && card.getBoundingClientRect().top < innerHeight);
        const anchorUrl = anchor?.dataset.productUrl;
        const top = anchor?.getBoundingClientRect().top;
        grid.innerHTML = pages.map((page) => page.grid.innerHTML).join('');
        this.pagesLoaded = pages.length;
        this.applyPageState(pages[pages.length - 1]);
        if (this.cachedRestore?.grid === grid) this.cachedRestore = null;
        const replacement = Array.from(grid.querySelectorAll('card-product, .card-product')).find((card) => card.dataset.productUrl === anchorUrl);
        if (replacement && Number.isFinite(top)) window.scrollBy({ top: replacement.getBoundingClientRect().top - top, behavior: 'auto' });
        try { sessionStorage.removeItem('clm_scroll_state'); } catch (_) {}
      } catch (error) {
        controller.abort();
        throw error;
      } finally {
        observer.disconnect();
        if (this.controller === controller) {
          this.loading = false;
          if (!this.cachedRestore) this.btnLoadMore?.removeAttribute('disabled');
        }
      }
    }

    async restoreScrollState(totalPages) {
      this.setProperties();
      if (totalPages <= this.pagesLoaded || this.isLastPage) return;
      if (!this.productGrid || !this.btnLoadMore?.dataset.href) throw new Error("Load more button is missing a next page link.");
      this.loading = true;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.btnLoadMore.setAttribute("disabled", "true");
      const grid = this.productGrid;
      try {
        const firstUrl = this.pageUrl(this.btnLoadMore.dataset.href);
        const firstPage = this.initialPage + this.pagesLoaded;
        if (this.pageNumber(firstUrl) !== firstPage) throw new Error("Collection page sequence changed.");
        const results = new Array(totalPages - this.pagesLoaded);
        let cursor = 0;
        const worker = async () => {
          while (cursor < results.length) {
            const index = cursor++;
            const url = new URL(firstUrl);
            url.searchParams.set("page", String(firstPage + index));
            results[index] = await this.fetchPage(url, signal);
          }
        };
        try {
          await Promise.all([worker(), worker()]);
        } catch (error) {
          this.controller.abort();
          throw error;
        }
        if (signal.aborted || grid !== document.querySelector("#product-grid")) throw new DOMException("Collection grid replaced.", "AbortError");
        const lastIndex = results.findIndex((page) => page.last);
        const pages = lastIndex < 0 ? results : results.slice(0, lastIndex + 1);
        grid.insertAdjacentHTML("beforeend", pages.map((page) => page.grid.innerHTML).join(""));
        this.pagesLoaded += pages.length;
        this.applyPageState(pages[pages.length - 1]);
      } finally {
        this.loading = false;
        this.btnLoadMore?.removeAttribute("disabled");
      }
    }

    async loadMoreProducts() {
      this.setProperties();
      if (this.loading || this.isLastPage || this.cachedRestore) return;
      if (!this.productGrid || !this.btnLoadMore?.dataset.href) throw new Error("Load more button is missing a next page link.");
      this.loading = true;
      this.controller = new AbortController();
      const grid = this.productGrid;
      const { signal } = this.controller;
      this.btnLoadMore.setAttribute("disabled", "true");
      try {
        const page = await this.fetchPage(this.btnLoadMore.dataset.href, signal);
        if (signal.aborted || grid !== document.querySelector("#product-grid")) throw new DOMException("Collection grid replaced.", "AbortError");
        grid.insertAdjacentHTML("beforeend", page.grid.innerHTML);
        this.pagesLoaded++;
        this.applyPageState(page);
      } finally {
        this.loading = false;
        this.btnLoadMore?.removeAttribute("disabled");
      }
    }
  }

  customElements.define("collection-load-more", CollectionLoadMore);
}
